import { AxiosPromise } from 'axios';
import { getBrowserInfo } from 'src/utils';
import { api } from 'src/services';
import { getAPiVersion } from './index';
import { ChatPhase } from 'src/models';

/**
 * Create a chat
 */
export type CreateChatApiPayload = {
  chatToken: string;
};

export type CreateChatResponse = {
  Chat: {
    appointment_id: number;
    browser_name: string;
    browser_type: string;
    chat_id: number;
    chat_open_time: string;
    chat_uuid: string;
    context: Record<string, unknown>;
    created_at: string;
    created_by: null;
    device_name: string;
    device_type: string;
    ip_address: string;
    last_modified: string;
    last_modified_by: null;
    phase: 'chat';
    phase_transitions: ['chat'];
    progress: number;
  };
  message: string;
};

export const createChatApi = ({
  chatToken,
}: CreateChatApiPayload): AxiosPromise<CreateChatResponse> => {
  const apiVersion = getAPiVersion();

  const browserInfo = getBrowserInfo();
  const payload = {
    access_token: chatToken,
    chat_open_time: new Date().toISOString(),
    browser_name: browserInfo.browserName,
    browser_type: browserInfo.browserType,
    device_type: browserInfo.deviceType,
    device_name: browserInfo.deviceName,
    ip_address: browserInfo.ipAddress,
  };

  if (apiVersion === 'v3') {
    return api.post('/v3/chat', payload);
  }

  return api.post('/v2/chats', payload);
};

export type ToolCall<Args = Record<string, unknown>> = {
  type: 'function';
  name: string;
  id: string;
  args?: Args;
};

/**
 * Describes the kind of data in ToolResponse.content:
 * - text: Plain string (human-readable message, error, or raw text).
 * - json: JSON-serialized data; content is a string of valid JSON (e.g. API response, structured payload).
 * - patient_document_id: A document ID string pointing to a patient document. mime_type is already present with the PatientDocument.
 */
export type ToolResponse<Args = Record<string, unknown>> = ToolCall<Args> & {
  content: string | string[];
  content_type: 'text' | 'serialized_json' | 'patient_document_ids';
  mime_type?: string;
};

export type ChatMessage = {
  classification?: null;
  content: string;
  flagged?: boolean;
  phase?: 'chat';
  role: 'assistant' | 'user';
  timestamp: string;
  trace_id?: string;
  tool_calls?: ToolCall[];
  tool_responses?: ToolResponse[];
};

export type StartChatApiPayload = {
  uuid: string;
};

export const startChatApi = ({
  uuid,
}: StartChatApiPayload): AxiosPromise<ChatMessage> => {
  const apiVersion = getAPiVersion();

  if (apiVersion === 'v3') {
    return api.post<ChatMessage>(`/v3/chat/start`, {});
  }

  return api.post(`/v2/chats/${uuid}/start`, {});
};

/**
 * Get Chat History
 */
export type GetChatHistoryApiPayload = {
  chatToken: string;
};

export type GetChatHistoryResponse = {
  allergies: string;
  chat_message: ChatMessage[];
  medications: string;
  progress: number;
};

export const getChatHistoryApi = ({
  chatToken,
}: GetChatHistoryApiPayload): AxiosPromise<GetChatHistoryResponse> => {
  const apiVersion = getAPiVersion();

  if (apiVersion === 'v3') {
    return api.get<GetChatHistoryResponse>(`/v3/chat/history`);
  }

  return api.post<GetChatHistoryResponse>(`/llm/chat_history`, {
    access_token: chatToken,
  });
};

/**
 * Send Response
 */
export type SendResponseApiPayload = {
  uuid: string;
  content: string;
  timestamp: string;
  chatToken: string;
  isComplete?: boolean;
  toolResponses?: ToolResponse[];
};

export type SendResponseApiResponse = {
  chat_end: boolean;
  context: Record<string, unknown>;
  progress: number;
  response?: ChatMessage;
  smart_replies?: [];
  chat_phase: ChatPhase;
  chat_transition?: boolean;
  chat_transition_message?: string;
};

export const sendResponseApi = ({
  uuid,
  content,
  timestamp,
  chatToken,
  isComplete,
  toolResponses,
}: SendResponseApiPayload): AxiosPromise<SendResponseApiResponse> => {
  const apiVersion = getAPiVersion();

  if (apiVersion === 'v3') {
    return api.post<SendResponseApiResponse>(`/v3/chat/response`, {
      dateTimeStr: timestamp,
      user_message: content,
      is_complete: isComplete,
      tool_responses: toolResponses,
    });
  }

  return api.post<SendResponseApiResponse>(`/llm/patient_chat`, {
    access_token: chatToken,
    dateTimeStr: timestamp,
    patient_input: content,
    isOver: isComplete,
    tool_responses: toolResponses,
  });
};

/**
 * Clear Chat
 */
export type ClearChatApiPayload = {
  chatToken: string;
  uuid: string;
};

export const clearChatApi = ({
  chatToken,
  uuid,
}: ClearChatApiPayload): AxiosPromise<void> => {
  const apiVersion = getAPiVersion();

  if (apiVersion === 'v3') {
    return api.post(`/v3/chat/reset`);
  }

  return api.put('/llm/clearChat', {
    access_token: chatToken,
  });
};

/**
 * Send Chat Feedback
 */
export type SendChatFeedbackApiPayload = {
  chatToken: string;
  score: 0 | 1;
  traceId: string;
};

export const sendChatFeedbackApi = ({
  chatToken,
  score,
  traceId,
}: SendChatFeedbackApiPayload): AxiosPromise<void> => {
  return api.post('/llm/chat_feedback', {
    access_token: chatToken,
    score,
    trace_id: traceId,
  });
};
